import type { AxiosError, AxiosResponse } from 'axios'
import qs from 'qs'
import type {
  DefaultResponse,
  PrintDefault,
  PrintError,
  ResErr,
  StrapiArrayResponse,
  StrapiError,
  StrapiSingleResponse,
} from 'src/types/common/api'

import type { TeacherCustomRequestConfig } from '@/utils/axios/maxios'

import { envUtils } from '../envUtils'
import { sendApiEvent } from './mixpanel'
import type { ApiErrorEventName, ApiEventName } from './schema/apiEvent.schema'
import type { EventPropertiesSchema } from './schema/eventProperties.schema'

type ApiEventProperties = EventPropertiesSchema.API응답 | EventPropertiesSchema.API에러
type ResponseConfig = AxiosResponse['config']

const MIXPANEL_SIZE_LIMIT = 1000000

const limitDataSize = <T extends { responseData: unknown }>(data: T) => {
  const dataSize = encodeURIComponent(JSON.stringify(data)).length
  if (dataSize < MIXPANEL_SIZE_LIMIT) {
    return data
  } else {
    const { responseData, ...rest } = data
    return rest
  }
}

const trackApiEvent = (
  eventName: ApiEventName | ApiErrorEventName,
  properties: ApiEventProperties,
) => {
  // 인터셉터와 화면 코드가 같이 보내는 이름은 화면 카탈로그에 화면 코드 쪽 프로퍼티 타입으로 있어 카탈로그 track 을 쓰지 않는다
  sendApiEvent(eventName, limitDataSize(properties), {
    send_immediately: envUtils.isLocalDev,
  })
}

const readEventName = (config: ResponseConfig) => {
  if (!config.url || !config.params || !('mp_event_name' in config.params)) return undefined
  const { mp_event_name } = config.params as { mp_event_name?: ApiEventName }
  return mp_event_name || undefined
}

const parseRequest = (config: ResponseConfig, fullUrl: string) => {
  const [url, _params] = fullUrl.split('?')
  const params = qs.parse(_params, { ignoreQueryPrefix: true })
  return {
    method: config.method as EventPropertiesSchema.API응답['method'],
    requestBody: config.data as unknown,
    requestParams: Object.keys(params).length > 0 ? params : null,
    requestUrl: url,
  }
}

// 인쇄 서버 주소는 요청마다 baseURL 로 정해진다. 이벤트에는 전체 주소를 싣는다
const printFullUrl = (config: ResponseConfig) => `${config.baseURL ?? ''}${config.url}`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const apiTrack = (response: AxiosResponse<DefaultResponse<any>>) => {
  const eventName = readEventName(response.config)
  if (!eventName) return
  trackApiEvent(eventName, {
    ...parseRequest(response.config, response.config.url ?? ''),
    responseData: response.data.data,
    status: response.status,
    ...(response.config as TeacherCustomRequestConfig).mpEventProps,
  })
}

export const errorTrack = ({ response }: AxiosError<ResErr>) => {
  if (!response) return
  const eventName = readEventName(response.config)
  if (!eventName) return
  trackApiEvent(`${eventName}_err`, {
    ...parseRequest(response.config, response.config.url ?? ''),
    responseData: response.data.code,
    responseMessage: response.data.message,
    status: response.status,
    ...(response.config as TeacherCustomRequestConfig).mpEventProps,
  })
}

export const strapiTrack = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response: AxiosResponse<StrapiArrayResponse<any>> | AxiosResponse<StrapiSingleResponse<any>>,
) => {
  const eventName = readEventName(response.config)
  if (!eventName) return
  trackApiEvent(eventName, {
    ...parseRequest(response.config, response.config.url ?? ''),
    responseData: Array.isArray(response.data.data)
      ? response.data.data.map(({ attributes }) => attributes)
      : response.data.data.attributes,
    status: response.status,
  })
}

export const strapiErrorTrack = ({ response }: AxiosError<StrapiError>) => {
  if (!response) return
  const eventName = readEventName(response.config)
  if (!eventName) return
  trackApiEvent(`${eventName}_err`, {
    ...parseRequest(response.config, response.config.url ?? ''),
    responseData: response.data.error.name,
    responseMessage: response.data.error.message,
    status: response.status,
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const printApiTrack = (response: AxiosResponse<PrintDefault<any>>) => {
  const eventName = readEventName(response.config)
  if (!eventName) return
  trackApiEvent(eventName, {
    ...parseRequest(response.config, printFullUrl(response.config)),
    responseData: response.data.data,
    status: response.status,
  })
}

export const printErrorTrack = ({ response }: AxiosError<PrintError>) => {
  if (!response) return
  const eventName = readEventName(response.config)
  if (!eventName) return
  trackApiEvent(`${eventName}_err`, {
    ...parseRequest(response.config, printFullUrl(response.config)),
    responseData: undefined,
    responseMessage: response.data.message,
    status: response.status,
  })
}
